import React from "react";
import {Platform} from "../data/entities/PlatformEntity";
import {loadScript} from "../utils/browser";

interface Props {
    environment: string;
    platform: Platform;
    locale: string;
    spaceId: string;
    genomeId: string;
}

export interface AdobeAnalyticsContextData {
    track: (eventType: EventType, data: object) => void;
}

type EventType = "pageevent" | "clickevent" | "accountevent" | "searchevent";

interface AdobeAnalyticsSDK {
    readonly init: (data: object) => void;
    readonly track: (eventType: EventType, data: object) => void;
}

export const AdobeAnalyticsContext = React.createContext({
    track: () => {}
} as AdobeAnalyticsContextData);

export default class AdobeAnalyticsProvider extends React.Component<Props, AdobeAnalyticsContextData> {
    SDK!: AdobeAnalyticsSDK;
    waData: object = {};
    initialized: boolean = false;
    pageEventTracked: boolean = false;

    private resourceUrls: object = {
        UAT: "https://ubistatic-a.akamaihd.net/0106/uat/global/tracking/analyticssdk.js",
        PREPROD: "https://ubistatic-a.akamaihd.net/0106/prod/global/tracking/analyticssdk.js",
        PROD: "https://ubistatic-a.akamaihd.net/0106/prod/global/tracking/analyticssdk.js",
    };

    track = async (eventType: EventType, data: object) => {
        const retryTimeout: number = 500;
        const maxRetries: number = 10;

        let tracked: boolean = false;
        let retries: number = 0;

        let pushTracking = () => {
            tracked = true;
            if (eventType === "pageevent") {
                this.pageEventTracked = true;
            }
            this.SDK.track(eventType, data);
        };

        let tryPushTracking = () => {
            if (this.initialized) {
                if (eventType === "pageevent") {
                    pushTracking();
                } else if (this.pageEventTracked) {
                    pushTracking();
                }
            }
            retries++;
        };

        (async () => {
            tryPushTracking();
            if (!tracked) {
                let interval = setInterval(() => {
                    tryPushTracking();
                    if (tracked || retries === maxRetries) {
                        clearInterval(interval);
                    }
                }, retryTimeout);
            }
        })().then(() => console.log(`${eventType} sent`));
    };

    state: AdobeAnalyticsContextData = {
        track: this.track
    }

    componentDidMount(): void {
        if (this.resourceUrls[this.props.environment]) {
            this.initData().then(() => {
                loadScript(this.resourceUrls[this.props.environment]).then(this.initSDK);
            });
        }
    }

    render(): JSX.Element {
        return (
            <AdobeAnalyticsContext.Provider value={this.state}>
                {this.props.children}
            </AdobeAnalyticsContext.Provider>
        );
    }

    initData = async () => {
        this.waData = {
            siteName: "uplay plus vault",
            siteType: "subscription",
            environment: this.props.environment,
            siteSection: "homepage",
            pageName: "main",
            template: this.props.platform,
            disableMedia: this.props.platform !== "web",
            enablePrivacy: true,
            language: AdobeAnalyticsProvider.getLocaleLanguage(this.props.locale),
            country: AdobeAnalyticsProvider.getLocaleCountry(this.props.locale),
            spaceId: this.props.spaceId,
            genomeId: this.props.genomeId,
        };

        (window as any).wa_data = this.waData;

        window.addEventListener("message", (event) => {
            if (event.data === "S_CODE_READY") {
                this.initialized = true;
            }
        });
    };

    initSDK = () => {
        this.SDK = (window as any).adobeAnalytics;
        this.SDK.init(this.waData);
    };

    static getLocaleLanguage(locale: string): string  {
        return locale.split("-")[0] || "";
    }

    static getLocaleCountry(locale: string): string  {
        return locale.split("-")[1] || "";
    }
}